<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->
<template>
  <div class="minimap-container">
    <div class="minimap-header">
      <span class="minimap-title">{{ $t("dashboardAdmin.minimap") }}</span>
      <PrimeButton
        icon="ri-close-line"
        text
        rounded
        size="small"
        @click="$emit('close')"
        severity="secondary"
      />
    </div>
    <div class="minimap" @click="$emit('minimap-click', $event)">
      <div
        class="minimap-grid"
        :style="{
          gridTemplateColumns: `repeat(${gridSettings.columns}, 1fr)`,
          gridTemplateRows: `repeat(${gridSettings.rows}, 1fr)`,
        }"
      >
        <div
          v-for="block in blocks"
          :key="'mini-' + block.id"
          class="minimap-block"
          :class="{ 'minimap-selected': selectedBlockId === block.id }"
          :style="{
            gridColumn: `${block.gridPosition.colStart} / ${block.gridPosition.colEnd}`,
            gridRow: `${block.gridPosition.rowStart} / ${block.gridPosition.rowEnd}`,
          }"
          v-tooltip.top="block.title || block.type"
        >
          <span class="minimap-block-title">{{
            block.title || block.type
          }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Button from "primevue/button";
import Tooltip from "primevue/tooltip";

export default {
  name: "MinimapPanel",
  components: {
    PrimeButton: Button,
  },
  directives: {
    tooltip: Tooltip,
  },
  props: {
    gridSettings: {
      type: Object,
      required: true,
    },
    blocks: {
      type: Array,
      required: true,
      default: () => [],
    },
    selectedBlockId: {
      type: String,
      default: null,
    },
  },
  emits: ["minimap-click", "close"],
};
</script>

<style scoped>
.minimap-container {
  position: fixed;
  bottom: 1rem;
  right: 420px;
  width: 250px;
  background: var(--p-content-background);
  border: 2px solid var(--p-content-border-color);
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  overflow: hidden;
}

.minimap-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  background: var(--p-content-background);
  border-bottom: 1px solid var(--p-content-border-color);
}

.minimap-title {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--p-text-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.minimap {
  padding: 0.75rem;
  cursor: pointer;
  background: var(--p-surface-ground);
}

.minimap-grid {
  display: grid;
  gap: 2px;
  width: 100%;
  aspect-ratio: 1;
  background: var(--p-content-border-color);
  border: 1px solid var(--p-content-border-color);
}

.minimap-block {
  background: var(--p-primary-200);
  border: 1px solid var(--p-primary-300);
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  overflow: hidden;
  position: relative;
}

.minimap-block:hover {
  background: var(--p-primary-300);
}

.minimap-block.minimap-selected {
  background: var(--p-primary-500);
  border-color: var(--p-primary-600);
  box-shadow: 0 0 0 2px var(--p-primary-100);
}

.minimap-block-title {
  font-size: clamp(6px, 1.5vw, 9px);
  font-weight: 600;
  color: var(--p-primary-900);
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  line-height: 1.2;
  user-select: none;
}

.minimap-block.minimap-selected .minimap-block-title {
  color: var(--p-primary-contrast-color);
}
</style>
